import { CommonModule } from "@angular/common";
import { Component, ElementRef, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { TranslocoDirective } from "@jsverse/transloco";
import { ButtonModule } from "primeng/button";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { StyleClassModule } from "primeng/styleclass";
import { TooltipModule } from "primeng/tooltip";
import { filter, map, Observable } from "rxjs";
import { AppBreadcrumbComponent } from "./app.breadcrumb";
import { NotificationKind } from "../../../../domain/notification";
import { NavigationService } from "../../../../services/navigation.service";
import { OrganizationService } from "../../../../services/organization.service";
import { UserService } from "../../../../services/user.service";
import { AppProfileMenuComponent } from "../../components/profile-menu/profile-menu.component";
import { LayoutService } from "../service/layout.service";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "[app-topbar]",
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    StyleClassModule,
    AppBreadcrumbComponent,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    AppProfileMenuComponent,
    TranslocoDirective,
  ],
  template: `<ng-container *transloco="let t">
    <div class="layout-topbar">
      <div class="topbar-start">
        <div class="block lg:hidden">
          <button #menubutton type="button" class="topbar-menubutton p-link p-trigger" (click)="onMenuButtonClick()">
            <i class="pi pi-bars"></i>
          </button>
        </div>
        <div class="flex flex-row flex-wrap items-center gap-1 md:gap-3">
          @if (isInOrganizationsRoute && currentOrgName) {
            <p-button
              [rounded]="true"
              (click)="onOrgButtonClick()"
              [pTooltip]="t('app.top-bar.changeOrganization')"
              tooltipPosition="bottom"
            >
              <i class="pi pi-building" style="width: 18px"></i>
              <span class="text-base"> {{ getOrgChangeButtonText(currentOrgName) }} </span>
              <i class="pi pi-chevron-right text-xs"></i>
            </p-button>
          }
          @if (isInTransactionOverviewRoute) {
            <p-button [rounded]="true" (click)="navigateBack()">
              <i class="pi pi-chevron-left text-sm text-center"></i>
              <span class="text-base text-center"> {{ t("app.common.back") }} </span>
            </p-button>
          }
          <nav app-breadcrumb class="topbar-breadcrumb w-auto"></nav>
        </div>
      </div>

      <div class="topbar-end">
        <ul class="topbar-menu">
          @if (hasMultipleOrganizations$ | async) {
            <li>
              <div class="relative">
                <a routerLink="/transaction-overview" class="w-4 h-4">
                  <p-button
                    icon="pi pi-list-check text-xl"
                    [text]="true"
                    [pTooltip]="t('app.top-bar.processOverview')"
                    tooltipPosition="bottom"
                  />
                </a>
                @if (transactionNotificationCount$ | async; as count) {
                  @if (count > 0) {
                    <span
                      class="absolute -top-1 left-[18px] inline-flex items-center justify-center min-w-[18px] min-h-[18px] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full border-2 border-white dark:border-surface-950"
                    >
                      {{ count > 99 ? "99+" : count }}
                    </span>
                  }
                }
              </div>
            </li>
          }
          <li>
            <app-profile-menu></app-profile-menu>
          </li>
        </ul>
      </div>
    </div>
  </ng-container>`,
})
export class AppTopbarComponent {
  @ViewChild("menubutton") menuButton!: ElementRef;

  public activeOrganization$;
  public hasMultipleOrganizations$: Observable<boolean>;
  public currentOrgName: string | null = null;
  public isInOrganizationsRoute = false;
  public transactionNotificationCount$: Observable<number>;
  public isInTransactionOverviewRoute = false;

  constructor(
    public layoutService: LayoutService,
    public router: Router,
    private organizationService: OrganizationService,
    private userService: UserService,
    private navigationService: NavigationService,
  ) {
    this.activeOrganization$ = this.organizationService.activeOrganization$;
    this.activeOrganization$.pipe(takeUntilDestroyed()).subscribe((org) => {
      this.currentOrgName = org?.name ?? null;
    });
    this.hasMultipleOrganizations$ = this.userService.getSelfOrganizations$().pipe(
      map((orgs) => orgs.length > 1),
      takeUntilDestroyed(),
    );

    this.transactionNotificationCount$ = this.userService.getSelfNotifications$().pipe(
      map((notifications) => notifications.filter((n) => n.kind === NotificationKind.Transaction).length),
      takeUntilDestroyed(),
    );

    // Check if we're in organizations or transaction overview routes
    this.checkOrganizationsRoute();
    this.checkTransactionOverviewRoute();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.checkOrganizationsRoute();
        this.checkTransactionOverviewRoute();
      });
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  onOrgButtonClick() {
    this.organizationService.clearOrganization();
    this.router.navigate(["/organizations/select"]);
  }

  navigateBack() {
    this.navigationService.navigateBack();
  }

  private checkOrganizationsRoute() {
    this.isInOrganizationsRoute = this.router.url.startsWith("/organizations");
  }

  private checkTransactionOverviewRoute() {
    this.isInTransactionOverviewRoute = this.router.url.includes("transaction-overview");
  }

  getOrgChangeButtonText(orgName: string): string {
    if (this.layoutService.isMobile() && orgName.length > 18) {
      return orgName.slice(0, 17) + "...";
    }
    return orgName;
  }
}
